import { MBSentry } from '@shared/util/sentry';
import { parseStoredJson } from './parseStoredJson';

export const Storage = {
  get(key: string, defaultValue?: string): string | undefined {
    const value = localStorage.getItem(key);
    return value ?? defaultValue;
  },

  getJSON<T = object>(key: string, defaultValue?: T): T | undefined {
    const value = localStorage.getItem(key);
    return parseStoredJson(value, defaultValue);
  },

  /**
   * Writes broadcast a synthetic `storage` event so hooks in the current tab
   * get the updated value, similar to the behavior in other tabs.
   */
  set(key: string, value: string): void {
    if (setItem(key, value)) {
      broadcast(key, value);
    }
  },

  setJSON<T = object>(key: string, value: T): void {
    const newValue = JSON.stringify(value);
    if (setItem(key, newValue)) {
      broadcast(key, newValue);
    }
  },

  remove(key: string): void {
    if (removeItem(key)) {
      broadcast(key, null);
    }
  },

  /**
   * Remove a key without broadcasting. The broadcast in `remove` runs every
   * listener synchronously, so it is unsafe from a render phase - use this only
   * where nothing can be holding the value (see the TTL eviction in
   * `JsonStorage.get`).
   */
  removeWithoutBroadcast(key: string): void {
    removeItem(key);
  },

  keys(): string[] {
    return Object.keys(localStorage);
  },
};

/**
 * `setItem` throws on a full quota and in Safari private browsing. Storage here
 * is always a cache or a UI preference, so a failed write degrades rather than
 * breaks.
 *
 * @returns whether the write landed, so callers only broadcast a change that
 * happened.
 */
function setItem(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    reportFailure(error, 'setItem', key, value.length);
    return false;
  }
}

function removeItem(key: string): boolean {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    reportFailure(error, 'removeItem', key);
    return false;
  }
}

function reportFailure(error: unknown, operation: string, key: string, valueLength?: number) {
  MBSentry.exception({
    error: error instanceof Error ? error : new Error(String(error)),
    // No value - it may hold user content.
    data: { operation, key, valueLength },
  });
}

function broadcast(key: string, newValue: string | null) {
  window.dispatchEvent(new StorageEvent('storage', { key, newValue }));
}
